/**
 * Weight Freeze (v2) — SoP (Standard Operating Procedure) copy.
 *
 * Every string below is business-rule text, not UI copy. It is carried
 * verbatim from the offline mockup's own SoP module (source doc 4.c.iv,
 * `Proship-Merchant-Panel.html` lines 24858-25369) — the two conditions,
 * the seven-step procedure, the account-standing copy. A handful of
 * fields (retention, packing band, the disabled tip, the sub-category
 * default) quote sentences that live in the mockup's DWS and Packing
 * modules instead, because those sentences are the same business rule the
 * SoP page has to state and a future DWS/Packing section will need again —
 * this file is the one place that wording is written down.
 *
 * Do not paraphrase, tighten, modernise or "fix" any sentence here. If a
 * sentence reads awkwardly, that is the source document, not a typo.
 */
import {MERCHANT} from './noberoFreeze'
import {REJECT_REASON} from './reference'

/** Verbatim — source: `SopPRELIM`. The two-cycle preliminary sentence. */
const PRELIMINARY = 'This approval is preliminary for the first 2 billing cycles post weight-freeze. '
  + 'Any deviations over and above will be handled as part of monthly billing.'

/**
 * Verbatim — the 3-submission budget wording. This is a substring of step
 * 5's own "needed" text below, not the whole of it: the source's full
 * sentence there opens with "A response when Prozo asks. " before this.
 */
const SUBMISSIONS = 'A request carries 3 submissions in total, being the 1 initial '
  + 'submission plus 2 additional rounds. The counter is on the request itself, and when it runs '
  + 'out the option to add data goes away.'

/** Verbatim — source: `DwsRETAIN` (mockup's DWS module). The 10-records / 6-months retention sentence. */
const RETENTION = 'Each SKU keeps its 10 most recent records, the courier’s and yours together, '
  + 'for up to 6 months. Older ones drop off, so evidence you upload today will not last forever.'

/** Verbatim — source: step 2's own "needed" text (see SOP_STEPS below). The 8-10 packing-materials guidance. */
const PACKING_BAND = 'Your packing material list declared in full, typically '
  + '8 to 10 types per brand, each one recorded with its dimensions if it is a carton or its size '
  + 'if it is a flyer, plus its own self weight.'

/** Verbatim — source: `DwsAPI_TIP` (mockup's DWS module). Exact string, including the em dash. */
const DISABLED = 'Disabled — Prozo business decision.'

/**
 * Verbatim — source: `PkFREEZE_NOTE` (mockup's Packing module).
 * Belongs to the Packing section, not SoP — a future Packing screen renders
 * this via `POLICY.subcatDefault`. Do not surface it on SopPage.
 */
const SUBCAT_DEFAULT = 'Weight freeze is done at the sub-category level as the business default. '
  + 'SKU-level freeze is technically possible. Both are subject to correct packing-material usage.'

export const POLICY = {
  preliminary: PRELIMINARY,
  submissions: SUBMISSIONS,
  retention: RETENTION,
  packingBand: PACKING_BAND,
  disabled: DISABLED,
  subcatDefault: SUBCAT_DEFAULT,
  /** The three reject reasons, in source order — reused from data/reference.js, not re-typed. */
  rejectReasons: Object.values(REJECT_REASON),
}

/**
 * Verbatim — source: `SopWHO` / `SopBILLING`. Keyed by `MERCHANT.type.freeze`
 * and `MERCHANT.type.billing` respectively.
 *
 * NOT RENDERED ANYWHERE at present. These fed the account-type banner at the top
 * of SopPage, which was removed on request along with the rest of that page's
 * header. Kept rather than deleted because between them they are the panel's only
 * statement of who raises a freeze on this account and what the wallet is debited
 * on — the copy is one screen away from being restored, and re-deriving it from
 * the source document would be the expensive way back.
 */
export const ACCOUNT_WHO = {
  prozo: 'Prozo Ops raises the freezes here; you follow each one and its outcome.',
  merchant: 'You raise the freezes here; each takes effect once Prozo approves it.',
  none: 'No freeze runs here yet, so every shipment bills on the courier reading.',
}
export const ACCOUNT_BILLING = {
  prepaid: 'Wallet is deducted on the final billed weight.',
  postpaid: 'Billed weight goes on the monthly invoice.',
}

/**
 * Verbatim — source: the footer CTA line, which reads differently when nothing
 * is frozen yet.
 *
 * NOT RENDERED ANYWHERE at present, for the same reason as ACCOUNT_WHO above:
 * SopPage's footer CTA was removed on request. Kept because it is source copy,
 * not something a future reader could safely reinvent.
 */
export const FOOTER_NOTE = {
  none: 'Nothing is frozen here yet. Step 4 is where the first request starts.',
  active: 'A freeze is watched for as long as it stands — step 7 is what happens if it stops holding.',
}

/** Verbatim — source: the two `SopCondition` cards. */
export const SOP_CONDITIONS = [
  {
    tag: 'Condition 1',
    title: 'Use the packing material you declared',
    body: 'Dead weight follows the product, but volumetric weight follows the box, so the agreed '
      + 'weight is only true for the material it was agreed against. Ship the same SKU in a '
      + 'different carton or flyer and the freeze does not cover that shipment: it bills on the '
      + 'weight the courier measures. Tell Prozo before packing changes, not after.',
    actionLabel: 'Review packing materials',
    section: 'packing',
  },
  {
    tag: 'Condition 2',
    title: 'Prozo approves before a freeze takes effect',
    body: 'A freeze you raise yourself is a proposal, not a setting. It changes nothing about '
      + 'billing until Prozo approves it and sets an effective date, and the approval is itself '
      + 'preliminary for the first two billing cycles. A Prozo-initiated freeze goes through '
      + 'the same review; only the side that raises it differs.',
    actionLabel: 'See where requests stand',
    section: 'requests',
  },
]

/**
 * Verbatim — source: `SopSteps(m, f)`. Every purpose/who/needed/next string
 * below is unchanged from the document. Only step 4's `who`/`owner` resolve
 * the source's freeze-type branch for the one merchant this port serves
 * (`MERCHANT.type.freeze === 'prozo'`) — the same substitution the source
 * itself performs for `m.name`, not a paraphrase.
 */
function buildSteps(merchant) {
  const freeze = (merchant.type && merchant.type.freeze) || 'none'
  const merchantRaises = freeze === 'merchant'
  const noFreeze = freeze === 'none'

  const step4Owner = noFreeze ? 'You or Prozo Ops' : merchantRaises ? 'You' : 'Prozo Ops'
  const step4Who = noFreeze
    ? 'Either side can start one. A merchant-raised freeze needs Prozo approval before it bills; a '
      + 'Prozo-raised freeze is reviewed the same way, from the other side of the desk.'
    : merchantRaises
      ? 'You. ' + merchant.name + ' is set up for merchant-initiated freezes, so requests start here and '
        + 'go to Prozo for approval.'
      : 'Prozo Ops. ' + merchant.name + ' is set up for Prozo-initiated freezes, so Prozo raises the '
        + 'request and you see it here from the moment it is raised.'

  return [
    {
      n: 1,
      section: 'catalogue',
      title: 'Get the catalogue clean',
      owner: 'You',
      link: 'Open catalogue',
      purpose: 'A frozen weight points at a catalogue entry, so the entry has to be right before the '
        + 'weight can be. This is the step people skip and then wonder why the freeze covers half '
        + 'the shipments they expected.',
      who: 'You, with the Prozo catalogue team on request. Prozo cannot merge duplicates on your behalf, '
        + 'because only you know which two names are the same product.',
      needed: 'Every SKU you actually ship present in the catalogue. Any SKU that Prozo auto-added from a '
        + 'shipment label reviewed and given a proper name. Mother and child SKUs that differ only by '
        + 'nomenclature merged into one entry.',
      next: 'A clean catalogue is what lets each SKU be sorted into a sub-category in step 2. An unmerged '
        + 'duplicate splits one product\'s shipment history in two, and neither half then carries enough '
        + 'evidence to freeze.',
    },
    {
      n: 2,
      section: 'packing',
      title: 'Define sub-categories and packing',
      owner: 'You',
      link: 'Open sub-categories and packing',
      purpose: 'A weight freeze is agreed against a sub-category and a packing material together. '
        + 'Neither one means much on its own: the product decides the dead weight, the box decides '
        + 'the volumetric weight.',
      who: 'You. Prozo preloads the standard sets, for example adult shoes size 6 to 9, kids shoes, '
        + 't-shirts and winter jackets or hoodies, and you extend them to fit your range.',
      needed: 'Every SKU mapped to a sub-category. ' + PACKING_BAND,
      next: 'Sub-category plus packing material becomes the subject of the request in step 4. It is also '
        + 'the condition the approval hangs on, so a material you forget to declare is a material the '
        + 'freeze will not cover.',
    },
    {
      n: 3,
      section: 'dws',
      title: 'Gather the evidence',
      owner: 'Prozo DWS',
      link: 'Open DWS data',
      purpose: 'A freeze is an evidence-led agreement. The evidence is the dimensioning and weighing '
        + 'system reading taken at the hub, with the image that goes with it.',
      who: 'Prozo, automatically, every time a shipment passes the DWS at the hub. You do not upload '
        + 'anything here, you select from what has already been captured.',
      needed: 'DWS images for the shipments you want to freeze, on the SKUs or the sub-category you are '
        + 'about to raise. More shipments and a tighter weight spread make a stronger request. A thin '
        + 'sample on a product that weighs three different things is what triggers a request for more data.',
      next: 'You attach the evidence to the request in step 4. Prozo reviews the request against the same '
        + 'images, so what you can see here is what the reviewer sees.',
    },
    {
      n: 4,
      section: 'initiate',
      title: 'Raise the request',
      owner: step4Owner,
      link: 'Open initiate weight freeze',
      purpose: 'The request states the weight you want billed and the conditions you are willing to hold '
        + 'to. Raise it at sub-category level by default: fewer and broader freezes are easier to '
        + 'approve and far easier to keep to.',
      who: step4Who,
      needed: 'The level, being sub-category by default and SKU or combo only where a product genuinely '
        + 'behaves differently from its neighbours. The dead, volumetric and chargeable weights you are '
        + 'asking for. The packing material the freeze is conditional on. The DWS evidence from step 3.',
      next: 'It goes to Prozo review as an open request. Nothing about billing changes yet. Raising a '
        + 'request has no effect on any shipment until step 6.',
    },
    {
      n: 5,
      section: 'requests',
      title: 'Prozo reviews it',
      owner: 'Prozo Ops',
      link: 'Open requests and statuses',
      purpose: 'Prozo checks the requested weight against the shipments it can see. Where the evidence is '
        + 'thin, it asks rather than declines, and the asking is bounded.',
      who: 'Prozo Ops reviews. You respond if more is asked for.',
      needed: 'A response when Prozo asks. ' + SUBMISSIONS,
      next: 'One of two things. Approval, which is step 6. Or a decline, for one of exactly three reasons: '
        + POLICY.rejectReasons.join('; ') + '. A declined request cannot be reopened, edited or '
        + 'copied. The only way forward is a fresh request that starts blank, with a new budget of 3 '
        + 'submissions.',
    },
    {
      n: 6,
      section: 'requests',
      title: 'Conditional approval, then two cycles',
      owner: 'Prozo Ops',
      link: 'See approved freezes',
      purpose: 'Approval is conditional, not absolute, and it does not become firm on day one. The first '
        + 'two billing cycles are a settling period for both sides.',
      who: 'Prozo Ops approves and sets the effective date. From that date the frozen weight is what bills.',
      needed: 'That the shipments actually go out in the declared packing material. That is the whole '
        + 'condition, and it is the one thing that can quietly stop being true.',
      next: PRELIMINARY + ' Once those two cycles have run, the freeze is fully in force and bills as '
        + 'agreed, with no cycle-end reconciliation attached to it.',
    },
    {
      n: 7,
      title: 'Monitoring, and reversal',
      owner: 'Prozo and you',
      purpose: 'A freeze is a standing agreement, so it is watched for as long as it stands. The usual '
        + 'reason one is withdrawn is not a dispute, it is a packing change nobody mentioned.',
      who: 'Prozo monitors the measured weights against the frozen one, continuously. You tell Prozo '
        + 'before you change packing material, not after the invoice arrives.',
      needed: 'Measured weight staying close to the frozen weight, and the declared packing material still '
        + 'being the one in use. A widening spread on a frozen sub-category is the early signal.',
      next: 'If adherence holds, nothing happens and the freeze keeps billing. If measured weights drift '
        + 'persistently, or the packing no longer matches what was declared, Prozo can reverse the '
        + 'freeze. Those shipments then return to courier-measured weight, and a new freeze has to be '
        + 'raised from scratch against the packing you are actually using.',
    },
  ]
}

export const SOP_STEPS = buildSteps(MERCHANT)
